import { jwtDecode } from 'jwt-decode';
import config from '../config';
import {
  ROLE_BACKHAUL_READ,
  ROLE_BACKHAUL_WRITE,
  ROLE_DEVICE_CONFIG_WRITE,
  ROLE_PUBLIC_WIFI_READ,
  ROLE_PUBLIC_WIFI_WRITE,
  USER_TYPE_VIEWER,
} from './roles';

const SESSION_STORAGE_KEY = 'nms_auth_session';

let cachedSession = null;

function readStoredSession() {
  try {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeStoredSession(session) {
  if (!session) {
    localStorage.removeItem(SESSION_STORAGE_KEY);
    cachedSession = null;
    return;
  }
  cachedSession = session;
  localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
}

async function fetchAuthMe() {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No auth token');
  }
  const response = await fetch(`${config.apiUrl}/auth/me`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  const data = await response.json().catch(() => ({}));
  if (response.status === 401) {
    throw new Error('Session expired');
  }
  if (!response.ok) {
    throw new Error(data?.error || 'Failed to load authorization profile');
  }
  return {
    email: data.email || null,
    projectId: data.project_id || null,
    userType: data.user_type || 'regular',
    roles: Array.isArray(data.roles) ? data.roles : [],
  };
}

export const auth = {
  async login(email, password) {
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/v1/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();

      if (data.success && data.token) {
        localStorage.setItem('token', data.token);
        return { status: 'success' };
      }

      if (data.mfaPendingCredential && Array.isArray(data.mfaInfo) && data.mfaInfo.length > 0) {
        return {
          status: 'mfa_required',
          mfaPendingCredential: data.mfaPendingCredential,
          mfaEnrollmentId: data.mfaInfo[0].mfaEnrollmentId,
        };
      }

      return { status: 'error', message: data.message || 'Invalid email or password' };
    } catch (error) {
      console.error('Login error:', error);
      return { status: 'error', message: 'An error occurred during login' };
    }
  },

  async mfaVerifyFinalize(mfaPendingCredential, mfaEnrollmentId, verificationCode) {
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/v1/login/mfa/finalize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mfaPendingCredential, mfaEnrollmentId, verificationCode }),
      });
      const data = await response.json();

      if (data.success && data.token) {
        localStorage.setItem('token', data.token);
        return { status: 'success' };
      }

      return { status: 'error', message: data.message || 'MFA verification failed. Please try again.' };
    } catch (error) {
      console.error('MFA verify error:', error);
      return { status: 'error', message: 'An error occurred during MFA verification' };
    }
  },

  async fetchSession() {
    const session = await fetchAuthMe();
    writeStoredSession(session);
    return session;
  },

  getSession() {
    if (cachedSession) return cachedSession;
    cachedSession = readStoredSession();
    return cachedSession;
  },

  hasRole(role) {
    const session = this.getSession();
    return Array.isArray(session?.roles) && session.roles.includes(role);
  },

  canReadBackhaul() {
    return this.hasRole(ROLE_BACKHAUL_READ);
  },

  canWriteBackhaul() {
    return this.hasRole(ROLE_BACKHAUL_WRITE);
  },

  canWriteDeviceConfig() {
    return this.hasRole(ROLE_DEVICE_CONFIG_WRITE);
  },

  canWritePublicWifi() {
    return this.hasRole(ROLE_PUBLIC_WIFI_WRITE);
  },

  canReadPublicWifi() {
    return this.hasRole(ROLE_PUBLIC_WIFI_READ);
  },

  isViewer() {
    return this.getSession()?.userType === USER_TYPE_VIEWER;
  },

  isReadOnly() {
    return this.isViewer();
  },

  logout() {
    localStorage.removeItem('token');
    writeStoredSession(null);
    window.dispatchEvent(new CustomEvent('auth:unauthorized', {
      detail: { message: 'You have been logged out.' },
    }));
  },

  isAuthenticated() {
    const token = localStorage.getItem('token');
    if (!token) return false;

    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;

      if (decoded.exp && decoded.exp < currentTime) {
        console.warn('Token has expired, logging out user');
        this.logout();
        window.dispatchEvent(new CustomEvent('auth:unauthorized', {
          detail: { message: 'Your session has expired. Please log in again.' },
        }));
        return false;
      }

      if (decoded.exp && (decoded.exp - currentTime) < 300) {
        window.dispatchEvent(new CustomEvent('auth:expiring-soon', {
          detail: {
            message: 'Your session will expire soon. Please save your work.',
            expiresIn: Math.floor(decoded.exp - currentTime),
          },
        }));
      }

      return true;
    } catch (error) {
      console.error('Error checking token validity:', error);
      this.logout();
      return false;
    }
  },

  getToken() {
    const token = localStorage.getItem('token');

    if (token && !this.isAuthenticated()) {
      this.logout();
      return null;
    }

    return token;
  },

  getProjectId() {
    const session = this.getSession();
    return session?.projectId || null;
  },

  getUserInfo() {
    const session = this.getSession();
    if (!session) return null;
    return {
      email: session.email,
      name: session.email,
      userType: session.userType,
      roles: session.roles,
      projectId: session.projectId,
    };
  },
};
